import { apiClient, WidgetApiError, parseCouponRejection } from '../api/client.js';
import type { DepositCheckoutRequest, TicketCheckoutResponse, TicketQuoteResponse } from '../api/client.js';
import { resolveDepositForDate, resolveKidsDepositForDate, DEFAULT_PAYMENT_CURRENCY, PRIVACY_POLICY_VERSION } from '@yebo/shared';
import { DEPOSIT_COPY, classifyDepositFailure } from '../deposit-copy.js';
import { formatMoney } from '../utils/formatting.js';
import type { BookingState } from './booking-store.js';
import { nextStep, selectedEvent, isTicketedFlow, isRecurringFlow, isTieredBasketEmpty, fixedEventSlot, normalizeFieldStates, fieldStateOf } from './booking-store.js';

export type StateUpdater = (patch: Partial<BookingState>) => void;

/** Map a server (or client) ticket-quote reason to a user-facing message. */
function ticketQuoteMessage(reason?: string): string {
  switch (reason) {
    case 'party_below_min':
      return 'This event has a minimum party size.';
    case 'party_above_max':
      return 'This event has a maximum party size.';
    case 'tier_required':
      return 'Please choose a ticket option.';
    case 'invalid_tier':
      return 'That ticket option is no longer available.';
    case 'sold_out':
      return 'This event is sold out.';
    default:
      return 'Tickets are unavailable for this selection.';
  }
}

/**
 * R10.8 [P2] SCAFFOLD — merge the progressive fields (accessibility / seating /
 * occasion) into the booking notes. Returns the guest's freeform notes with any
 * populated progressive fields appended as labelled lines, so staff see them
 * without a wire-schema change. Empty/disabled fields are omitted. Pure +
 * idempotent so a retried submit produces identical notes.
 */
export function composeBookingNotes(state: BookingState): string {
  const lines: string[] = [];
  if (state.notes?.trim()) lines.push(state.notes.trim());
  // dietaryRestrictions is the guest-facing "Dietary restrictions" free-text
  // field (step-contact.ts) - distinct from dietaryDetails (the conditional
  // "please specify" follow-up, which is already sent as its own wire field).
  // Previously this was captured in widget state but never transmitted in any
  // form - found live 2026-07-26 (a guest's typed dietary restriction vanished
  // before reaching the reservation). Folded into notes here, matching the
  // same scaffold pattern as accessibility/seating/occasion below.
  //
  // #793 — a field the venue switched OFF never travels, even if a value is
  // somehow present in state (a stale value from before the switch, a tampered
  // client). The control isn't rendered, so this is belt-and-braces; the server
  // drops an off field as well.
  if (fieldStateOf(state, 'dietary') !== 'off' && state.dietaryRestrictions?.trim()) {
    lines.push(`Dietary restrictions: ${state.dietaryRestrictions.trim()}`);
  }
  // Accessibility needs is NOT one of the Settings-controlled fields (#792's
  // list) — it is unconditional, exactly as before.
  if (state.accessibilityNeeds?.trim()) lines.push(`Accessibility: ${state.accessibilityNeeds.trim()}`);
  if (fieldStateOf(state, 'seatingPreference') !== 'off' && state.seatingPreference?.trim()) {
    lines.push(`Seating: ${state.seatingPreference.trim()}`);
  }
  if (fieldStateOf(state, 'occasion') !== 'off' && state.occasion?.trim()) {
    lines.push(`Occasion: ${state.occasion.trim()}`);
  }
  return lines.join('\n');
}

/**
 * #793 — the `dietaryDetails` wire field, projected through the venue's setting:
 * a venue with Dietary OFF sends no key at all (the widget renders no control,
 * so the value should already be empty — this makes it unconditional).
 */
export function dietaryDetailsForWire(state: BookingState): string | undefined {
  if (fieldStateOf(state, 'dietary') === 'off') return undefined;
  return state.dietaryDetails || undefined;
}

/** Load widget configuration from the middleware */
export async function loadConfig(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  update({ loading: true, error: null });
  try {
    const config = await apiClient.getConfig(state.locationId);
    apiClient.setToken(config.widgetToken);
    update({
      loading: false,
      configLoaded: true,
      calendarId: config.calendarId || state.calendarId,
      timezone: config.timezone || state.timezone,
      businessName: config.businessName || state.businessName,
      // Retain the branding theme so the host can map it onto the --booking-*
      // CSS custom properties (applied in booking-widget's _applyThemeVars).
      theme: config.theme || null,
      requirePayment: config.requirePayment,
      // #480 §4.2 — the orthogonal "this venue takes deposits through
      // /deposit/checkout" flag. Read defensively and normalised to a strict
      // boolean: absent (today's config, or any tenant with the feature flag
      // off) MUST resolve to false, which is the dark state §11 guarantees.
      depositCheckout:
        (config as { depositCheckout?: boolean }).depositCheckout === true,
      paymentProvider: config.paymentProvider || 'none',
      stripePublicKey: config.stripePublicKey || '',
      paymentAmount: config.paymentAmount || 0,
      paymentCurrency: config.paymentCurrency || DEFAULT_PAYMENT_CURRENCY,
      depositConfig: config.depositConfig || null,
      workingHoursSchedules: config.workingHoursSchedules || [],
      // #947 — ends a fixed-date event's slot when the event has no `end`.
      defaultDuration:
        typeof config.defaultDuration === 'number' && config.defaultDuration > 0
          ? Math.floor(config.defaultDuration)
          : 90,
      events: config.events || [], // #159
      // #592 — contact details so a refusal is actionable.
      contactPhone: String(config.contactPhone || '').trim(),
      contactEmail: String(config.contactEmail || '').trim(),
      // #793 — the venue's Off / Optional / Required per optional-detail field.
      // Read defensively off the payload (the key is absent on an older server,
      // in which case every field resolves to `optional` = today's behaviour).
      fieldStates: normalizeFieldStates((config as { fields?: unknown }).fields),
      // #591 — the venue's online party cap clamps the who's-coming counters.
      maxOnlinePartySize:
        typeof config.maxOnlinePartySize === 'number' && config.maxOnlinePartySize >= 1
          ? Math.floor(config.maxOnlinePartySize)
          : 0,
    });
  } catch (err: any) {
    console.error('[yebo-widget] loadConfig failed:', err);
    let message: string;
    if (err?.status === 404) {
      message = 'Location not found. Please check the location-id attribute.';
    } else if (err instanceof TypeError || !err?.status) {
      message = 'Unable to connect to the booking server. This may be a CORS or network issue.';
    } else {
      message = `Booking service error (status ${err.status}). Please try again later.`;
    }
    update({
      loading: false,
      error: message,
    });
  }
}

// #524 — availability requests are now racy by construction: since #521 made
// the answer depend on party size, a guest holding the + button fires one
// request per click. They resolve in ARRIVAL order, so a slower earlier
// response (a smaller party, which has times) can land after the newer one
// (a larger party, which has none) and overwrite it — leaving the previous
// party's times on screen. Reproduced live: five clicks, five in-flight
// requests for partySize 3,4,5,6,7. Only the newest answer may be applied.
let availabilitySeq = 0;

/** Fetch available time slots for a given date */
export async function fetchAvailability(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  if (!state.selectedDate) return;
  const seq = ++availabilitySeq;

  update({
    loading: true,
    error: null,
    availableSlots: [],
    // Reset any prior recovery payload so a fresh date starts clean.
    availabilityReason: null,
    alternateTimes: [],
    alternateSections: [],
    nextAvailableDate: null,
  });
  try {
    const result = await apiClient.getAvailability({
      locationId: state.locationId,
      calendarId: state.calendarId,
      date: state.selectedDate,
      tz: state.timezone,
      partySize: state.partySize,
    });
    // Superseded while in flight — drop it rather than clobber a newer answer.
    if (seq !== availabilitySeq) return;
    update({
      loading: false,
      availableSlots: result.slots,
      // R10.4 — capture the optional empty-state enrichment when present. These
      // are only ever READ by the recovery block, which itself only renders for
      // an empty date under the V2 flag, so a bare (un-enriched) response from a
      // not-yet-updated backend leaves them at their reset defaults.
      availabilityReason: result.reason ?? null,
      alternateTimes: result.alternateTimes ?? [],
      alternateSections: result.alternateSections ?? [],
      nextAvailableDate: result.nextAvailableDate ?? null,
    });
  } catch (err) {
    console.error('[yebo-widget] fetchAvailability failed:', err);
    // A superseded request's failure must not surface an error over a newer,
    // successful answer.
    if (seq !== availabilitySeq) return;
    update({
      loading: false,
      error: 'Unable to load available times. Please try again.',
    });
  }
}

/**
 * #130 — lazily load the bookable occurrences of the selected recurring series.
 * Called when the guest opens the date step for a recurring card. No-op for a
 * non-recurring selection. The returned `occId`s are opaque — stored as-is and
 * later sent verbatim to quote/checkout/book.
 */
export async function fetchOccurrences(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  const event = selectedEvent(state);
  if (!event?.recurring) return;

  update({ occurrencesLoading: true, experienceOccurrences: [], error: null });
  try {
    const res = await apiClient.listOccurrences(state.locationId, event.id);
    update({
      occurrencesLoading: false,
      experienceOccurrences: (res.occurrences ?? []).map((o) => ({
        occId: o.occId,
        date: o.date,
        coversRemaining: o.coversRemaining,
        soldOut: o.soldOut,
      })),
    });
  } catch (err) {
    console.error('[yebo-widget] fetchOccurrences failed:', err);
    update({
      occurrencesLoading: false,
      experienceOccurrences: [],
      error: 'Unable to load available dates. Please try again.',
    });
  }
}

/**
 * R10.6 — submit the Join Notify form from the empty state. Posts the guest onto
 * the cancellation-recovery demand list. Never blocks the booking flow: on
 * failure it surfaces a typed message and leaves the guest on the empty state to
 * retry or pick another date.
 */
export async function submitJoinNotify(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  if (!state.selectedDate) return;
  update({ notifySubmitting: true, notifyError: null });
  try {
    const res = await apiClient.joinNotify({
      locationId: state.locationId,
      date: state.selectedDate,
      partySize: state.partySize,
      preferredTime: state.selectedSlot ? state.selectedSlot.slice(11, 16) : undefined,
      contact: {
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        phone: state.phone,
        notes: state.notes || undefined,
      },
      notes: state.notes || undefined,
      consent: state.consentGiven
        ? { accepted: true, policyVersion: PRIVACY_POLICY_VERSION, acceptedAt: new Date().toISOString() }
        : undefined,
    });
    update({
      notifySubmitting: false,
      notifySubmitted: res.ok !== false,
      notifyError: res.ok === false ? 'Could not join the notify list. Please try again.' : null,
    });
  } catch (err: any) {
    console.error('[yebo-widget] joinNotify failed:', err);
    update({
      notifySubmitting: false,
      notifyError:
        err?.status === 501
          ? 'The notify list is not available yet. Please try another date.'
          : 'Could not join the notify list. Please try again.',
    });
  }
}

/** Compute the total deposit amount based on per-person config or legacy flat amount */
function computeDepositAmount(state: BookingState): number {
  if (state.depositConfig && state.selectedDate) {
    const perPerson = resolveDepositForDate(state.depositConfig, state.selectedDate);
    const perKid = resolveKidsDepositForDate(state.depositConfig, state.selectedDate);
    const kidsCount = state.kidsCount || 0;
    const adults = state.partySize - kidsCount;
    return (perPerson * adults) + (perKid * kidsCount);
  }
  // Fallback to legacy flat amount
  return state.paymentAmount;
}

/** Request a Stripe PaymentIntent from the middleware (legacy/stripe provider) */
export async function requestPaymentIntent(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  const totalAmount = computeDepositAmount(state);
  if (!state.requirePayment || !totalAmount) return;

  update({ loading: true, error: null });
  try {
    const result = await apiClient.createPaymentIntent({
      locationId: state.locationId,
      amount: totalAmount,
      currency: state.depositConfig?.currency || state.paymentCurrency,
      // Booking context lets the server recompute and validate the deposit
      // amount rather than trusting this client-side total (#260/#286).
      partySize: state.partySize,
      kidsCount: state.kidsCount || 0,
      date: state.selectedDate || undefined,
    });
    update({
      loading: false,
      paymentClientSecret: result.clientSecret,
      paymentIntentId: result.paymentIntentId,
      currentStep: 'payment',
    });
  } catch {
    update({
      loading: false,
      error: 'Unable to set up payment. Please try again.',
    });
  }
}

/** Create a GHL order/invoice for payment */
export async function requestGhlPaymentOrder(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  const totalAmount = computeDepositAmount(state);
  if (!state.requirePayment || !totalAmount) return;

  update({ loading: true, error: null });
  try {
    const result = await apiClient.createGhlOrder({
      locationId: state.locationId,
      amount: totalAmount,
      currency: state.depositConfig?.currency || state.paymentCurrency,
      contact: {
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        phone: state.phone,
        notes: state.notes || undefined,
      },
      description: 'Reservation Deposit',
    });
    update({
      loading: false,
      ghlOrderId: result.orderId,
      ghlPaymentUrl: result.paymentUrl,
      contactId: result.contactId,
      currentStep: 'payment',
    });
  } catch {
    update({
      loading: false,
      error: 'Unable to set up payment. Please try again.',
    });
  }
}

/** Poll GHL order status — returns true if payment completed */
export async function pollGhlPaymentStatus(
  state: BookingState,
  update: StateUpdater,
): Promise<boolean> {
  if (!state.ghlOrderId) return false;

  try {
    const result = await apiClient.getGhlOrderStatus(state.ghlOrderId, state.locationId);
    if (result.status === 'paid' || result.status === 'completed') {
      update({ paymentStatus: 'succeeded' });
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Submit the booking to the middleware → GHL */
export async function submitBooking(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  update({ loading: true, error: null });
  // Stable per-attempt key so a retry/double-submit dedupes server-side (#290).
  const idempotencyKey = state.idempotencyKey
    || (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  if (!state.idempotencyKey) update({ idempotencyKey });
  // R10.8 [P2] SCAFFOLD — fold progressive fields (accessibility / seating /
  // occasion) into `notes` so they reach staff without a wire-schema change.
  // TODO(View 10 · R10.8 / View 13): promote these to first-class BookingRequest
  // fields once the lead adds them to @yebo/shared + the public-config allowlist
  // (do NOT widen shared types from this view — SendMessage the lead).
  const composedNotes = composeBookingNotes(state);
  try {
    const result = await apiClient.book({
      locationId: state.locationId,
      calendarId: state.calendarId,
      idempotencyKey,
      slot: {
        start: state.selectedSlot!,
        end: state.selectedSlotEnd!,
      },
      contact: {
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        phone: state.phone,
        notes: composedNotes || undefined,
      },
      partySize: state.partySize,
      kidsCount: state.kidsCount || undefined,
      highChairNeeded: state.highChairNeeded || undefined,
      dietaryDetails: dietaryDetailsForWire(state),
      notes: composedNotes || undefined,
      paymentIntentId: state.paymentIntentId || undefined,
      ghlOrderId: state.ghlOrderId || undefined,
      // #159 / #130 — the booking's event id. For a recurring series this is the
      // opaque occurrence id (`${seriesId}#${date}`); for a one-off event it is
      // `selectedEventId` exactly as before (the one-off path is unchanged: when
      // `isRecurringFlow` is false this evaluates to the identical value).
      eventId: (isRecurringFlow(state) ? state.selectedOccurrenceId : state.selectedEventId) || undefined,
      consent: state.consentGiven
        ? { accepted: true, policyVersion: PRIVACY_POLICY_VERSION, acceptedAt: new Date().toISOString() }
        : undefined,
    });
    update({
      loading: false,
      currentStep: 'confirm',
      appointmentId: result.appointmentId,
      contactId: result.contactId,
      confirmationNumber: result.confirmationNumber,
      // R10.7 — the server MAY return a tokened self-service manage link
      // (`GET /api/widget/reservation/:token`). Read defensively: absent until
      // the backend issues it, in which case the confirm step falls back to a
      // download-only Add-to-Calendar with no manage link.
      manageBookingUrl: (result as { manageBookingUrl?: string }).manageBookingUrl || '',
    });
  } catch (err) {
    update({
      loading: false,
      error: guestMessageFromError(err),
    });
  }
}

/**
 * Server-authored refusals are written FOR guests ("No online availability
 * for this time — please contact the restaurant directly", "dinner is fully
 * booked (...)") — surface them instead of a generic line, which hid the #504
 * capacity message behind "Booking failed" in the round-4 walk. Only
 * client-caused statuses qualify (validation/capacity); anything else — 5xx,
 * network, non-JSON — keeps the safe fallback. Length-capped so a malformed
 * body can't dump a wall of text into the widget.
 */
function guestMessageFromError(err: unknown): string {
  const FALLBACK = 'Booking failed. Please try again or contact us directly.';
  if (err instanceof WidgetApiError && [400, 402, 409, 422].includes(err.status)) {
    try {
      const parsed = JSON.parse(err.body) as { error?: unknown };
      if (typeof parsed?.error === 'string') {
        const msg = parsed.error.trim();
        if (msg.length > 0 && msg.length <= 200) return msg;
      }
    } catch {
      // non-JSON body — fall through to the generic line
    }
  }
  return FALLBACK;
}

// ── Guest deposits (#480 · design §5.2) ───────────────────────────────────────
// Contact → /deposit/checkout → payment → /deposit/verify → confirm.
//
// THE RULE THIS SLICE EXISTS FOR: a deposit that cannot be taken STOPS the
// booking and tells the guest. There is no path below on which a failed deposit
// falls through to a free, confirmed booking — that was the `catch {}` at
// booking-widget.ts:477 and it is gone.

/** A fresh idempotency key for a creation call (stable across retries once set). */
function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Build the `/deposit/checkout` body: the same BookingRequest shape `/book`
 * sends (so `validateBookingRequest` is reused verbatim server-side) plus the
 * REQUIRED idempotencyKey. It carries NO server-authoritative payment field —
 * no amount, currency, provider, captureMode, depositType, reservationId or
 * status. Those are priced and decided by the server alone (DR-PAYMENTS-01);
 * sending one is a 400 `client_supplied_payment_field`.
 */
function buildDepositCheckoutBody(
  state: BookingState,
  idempotencyKey: string,
): DepositCheckoutRequest {
  const composedNotes = composeBookingNotes(state);
  return {
    locationId: state.locationId,
    calendarId: state.calendarId,
    idempotencyKey,
    slot: {
      start: state.selectedSlot!,
      end: state.selectedSlotEnd!,
    },
    contact: {
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
      phone: state.phone,
      notes: composedNotes || undefined,
    },
    partySize: state.partySize,
    kidsCount: state.kidsCount || undefined,
    highChairNeeded: state.highChairNeeded || undefined,
    dietaryDetails: dietaryDetailsForWire(state),
    notes: composedNotes || undefined,
    // #159 / #130 — same event resolution as submitBooking. A ticketed event
    // never reaches here (isTicketedFlow wins in goToNextStep).
    eventId: (isRecurringFlow(state) ? state.selectedOccurrenceId : state.selectedEventId) || undefined,
    consent: state.consentGiven
      ? { accepted: true, policyVersion: PRIVACY_POLICY_VERSION, acceptedAt: new Date().toISOString() }
      : undefined,
  };
}

/**
 * Ask the server whether this booking owes a deposit and, if it does, hold the
 * seat and take a payment handle.
 *
 *  - Response A (`depositRequired: false`) → today's `/book`, unchanged.
 *  - Response B (deposit owed)             → payment step, driven by whichever
 *                                            handle came back (D6).
 *  - Response C (`settled: true`)          → straight to verify.
 *  - ANY failure                           → stay on `contact`, show why. The
 *                                            booking does not proceed.
 */
export async function submitDepositCheckout(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  const idempotencyKey = state.idempotencyKey || newIdempotencyKey();
  if (!state.idempotencyKey) update({ idempotencyKey });

  update({ loading: true, error: null });
  try {
    const res = await apiClient.depositCheckout(buildDepositCheckoutBody(state, idempotencyKey));

    // No deposit owed for THIS booking — nothing was created server-side, so
    // run the ordinary booking path with the same idempotency key.
    if (!res.depositRequired) {
      await submitBooking({ ...state, idempotencyKey }, update);
      return;
    }

    // Idempotent replay of an already-settled checkout: the money question is
    // answered, so skip the payment step and confirm through verify.
    if (res.settled) {
      const held: Partial<BookingState> = {
        depositRequired: true,
        depositReservationId: res.reservationId ?? null,
        depositDate: res.date ?? null,
        confirmationNumber: res.confirmationNumber ?? state.confirmationNumber,
        appointmentId: res.reservationId ?? state.appointmentId,
      };
      update(held);
      await verifyDepositPayment({ ...state, ...held, idempotencyKey }, update);
      return;
    }

    update({
      loading: false,
      error: null,
      currentStep: 'payment',
      depositRequired: true,
      depositReservationId: res.reservationId ?? null,
      depositDate: res.date ?? null,
      // VERBATIM. The hold window (R6) is a server constant; the widget never
      // derives, defaults or extends this instant.
      depositExpiresAt: res.expiresAt ?? null,
      confirmationNumber: res.confirmationNumber ?? null,
      appointmentId: res.reservationId ?? null,
      depositAmountCents: res.amountCents ?? 0,
      paymentAmount: res.amountCents ?? 0,
      paymentCurrency: res.currency || state.paymentCurrency,
      depositType: (res.depositType as BookingState['depositType']) ?? null,
      paymentStatus: 'idle',
      // The two handles. Exactly one is populated (the server asserts it) and
      // the payment step picks its renderer from whichever it is — never from
      // `res.provider`, which is diagnostic only.
      paymentClientSecret: res.clientSecret ?? null,
      depositAuthorizationUrl: res.authorizationUrl ?? '',
      depositPolicyNote: res.policyNote ?? null,
      // A deposit checkout prices itself — clear the legacy per-person deposit
      // framing so the payment step shows the server's amount, not a recompute.
      depositConfig: null,
    });
  } catch (err) {
    // B4 — THIS IS THE BUG THE ISSUE EXISTS TO FIX. A deposit that cannot be
    // set up must stop the booking and say so. No silent pass, on any path.
    //
    // #480 G3 — …and it must stay RECOVERABLE. A refusal the guest cannot
    // escape without reloading the page is its own dead end: state is
    // in-memory with no storage, so a burnt idempotency key used to mean the
    // booking was over. `classifyDepositFailure` decides which it is.
    console.error('[yebo-widget] deposit checkout failed:', err);
    const failure = classifyDepositFailure(err);
    update({
      loading: false,
      error: failure.message,
      // 'dead' ⇒ this key can never succeed again (the claim it points at is
      // unresolvable, or the checkout behind it is closed). Burn it, so the
      // next Continue is a genuinely new attempt.
      //
      // 'retryable' ⇒ KEEP the key. The guest is retrying the SAME attempt and
      // dedupe is exactly what the key is for: clearing it here is how one
      // impatient double-click becomes two reservations.
      ...(failure.attempt === 'dead' ? deadAttemptReset() : {}),
    });
  }
}

/**
 * Abandon the current deposit attempt IN PLACE (#480 G3), so the next submit is
 * a clean, brand-new booking — no reload, no lost contact details.
 *
 * Two things have to go together:
 *  1. the idempotency key, because a dead claim answers the same way forever;
 *  2. every handle to the reservation that key created, because it is not
 *     payable and must never be polled, verified or shown as confirmed.
 *
 * What is deliberately KEPT: the guest's slot, party and contact fields. They
 * re-submit with one click; making them retype is the same dead end wearing a
 * different hat.
 */
function deadAttemptReset(): Partial<BookingState> {
  return {
    idempotencyKey: '',
    depositRequired: false,
    depositReservationId: null,
    depositDate: null,
    depositExpiresAt: null,
    depositAuthorizationUrl: '',
    depositPolicyNote: null,
    depositAmountCents: 0,
    paymentClientSecret: null,
    paymentAmount: 0,
    paymentStatus: 'idle',
    confirmationNumber: null,
    appointmentId: null,
  };
}

/**
 * Settle + confirm a held deposit booking. Called after the card fields report
 * success, and on every 5 s poll while a hosted payment page is open (quiet).
 *
 * `quiet` mirrors verifyTicketPurchase: a not-yet-paid poll is the expected
 * answer and must not flash an error at the guest.
 */
export async function verifyDepositPayment(
  state: BookingState,
  update: StateUpdater,
  opts: { quiet?: boolean } = {},
): Promise<boolean> {
  if (!state.depositReservationId || !state.depositDate) {
    // Deliberately NOT a silent advance to `confirm` (that is the ticket path's
    // shortcut and it would confirm a booking we cannot prove was paid).
    if (!opts.quiet) {
      update({ loading: false, paymentStatus: 'failed', error: DEPOSIT_COPY.errors.nothingToVerify });
    }
    return false;
  }

  if (!opts.quiet) update({ loading: true, error: null });
  try {
    const res = await apiClient.verifyDeposit({
      locationId: state.locationId,
      reservationId: state.depositReservationId,
      date: state.depositDate,
    });

    if (res.success) {
      update({
        loading: false,
        error: null,
        paymentStatus: 'succeeded',
        currentStep: 'confirm',
        confirmationNumber: res.confirmationNumber || state.confirmationNumber,
        appointmentId: state.appointmentId || state.depositReservationId,
      });
      return true;
    }

    // Still pending. The server may refresh the hold deadline — keep it current.
    if (res.expiresAt && res.expiresAt !== state.depositExpiresAt) {
      update({ depositExpiresAt: res.expiresAt });
    }
    if (!opts.quiet) {
      update({ loading: false, paymentStatus: 'failed', error: DEPOSIT_COPY.errors.verifyFailed });
    }
    return false;
  } catch (err) {
    if (!opts.quiet) {
      console.error('[yebo-widget] deposit verify failed:', err);
      update({ loading: false, paymentStatus: 'failed', error: DEPOSIT_COPY.errors.verifyFailed });
    }
    return false;
  }
}

// ── Prepaid event ticketing ───────────────────────────────────────────────────

/**
 * #816 — the ONE place a promo code is normalised: trimmed and uppercased.
 *
 * Codes are case-insensitive and the server normalises too; doing it here means
 * the guest sees the same string the server matched, and the field, the quote
 * and the checkout can never disagree about which code is in play.
 */
export function normalizeCouponCode(raw: string | null | undefined): string {
  return typeof raw === 'string' ? raw.trim().toUpperCase() : '';
}

/**
 * Fetch an authoritative price quote + live availability for the selected
 * ticketed event. Called whenever the event, tier, or party size changes so the
 * displayed total always matches what the server will charge. No-op for
 * non-ticketed selections.
 */
export async function fetchTicketQuote(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  const event = selectedEvent(state);
  if (!event?.ticketing) {
    update({ ticketQuote: null, ticketAvailability: null, ticketQuoteError: null });
    return;
  }
  // #130 — a recurring series is only quotable once a specific occurrence is
  // chosen (the quote keys on the occId). Until then there is nothing to price,
  // so skip the call rather than firing a quote with no occurrence id.
  if (isRecurringFlow(state) && !state.selectedOccurrenceId) {
    update({ ticketQuote: null, ticketAvailability: null, ticketQuoteError: null });
    return;
  }

  // r93 X2 — a TIERED event is priced by its basket (#652). With nothing in it
  // there is nothing to price: quoting would fall back to the scalar fields (the
  // default party of 2 on the first tier) and show a total — and an auto deal —
  // for tickets the guest never chose. Clear instead, and bump the epoch so an
  // answer still in flight for the basket they just emptied is dropped. The
  // typed code stays in the field and re-applies once a ticket is added.
  if (isTieredBasketEmpty(state)) {
    ticketQuoteEpoch++;
    update({
      ticketQuote: null,
      ticketAvailability: null,
      ticketQuoteError: null,
      couponApplied: null,
      couponApplying: false,
      autoDeal: null,
    });
    return;
  }

  const couponCode = normalizeCouponCode(state.couponCode);
  await performTicketQuote(state, update, couponCode, { keepCouponError: false });
}

/**
 * r93 X2 + G3 N1 — the LATEST quote request wins. Bumped by every quote request,
 * by an emptied tiered basket, and by an event switch (`invalidateTicketQuotes`).
 * A quote that started under an older epoch describes a basket or an event the
 * guest has since left, so its answer is not written.
 */
let ticketQuoteEpoch = 0;

/** Discard every quote still in flight (e.g. the guest switched events). */
export function invalidateTicketQuotes(): void {
  ticketQuoteEpoch += 1;
}

/**
 * #816 — one quote round-trip with an explicit coupon code.
 *
 * Split out of `fetchTicketQuote` for one reason: a REFUSED code must not cost
 * the guest their booking. On a refusal the widget shows the SERVER's sentence
 * and immediately re-quotes at full price (`couponCode: ''`, `keepCouponError`
 * so the refusal stays on screen), leaving a purchasable order behind. The code
 * itself stays in the field so a typo can be corrected.
 */
async function performTicketQuote(
  state: BookingState,
  update: StateUpdater,
  couponCode: string,
  opts: { keepCouponError: boolean },
): Promise<QuotedTotal | null> {
  const event = selectedEvent(state);
  if (!event?.ticketing) return null;

  if (couponCode) update({ couponApplying: true });
  const epoch = ++ticketQuoteEpoch;
  try {
    const res = await apiClient.quoteTicket({
      locationId: state.locationId,
      // #130 — for a recurring series the booking id is the opaque occurrence id
      // (`${seriesId}#${date}`), NOT the series id. One-off events send `event.id`
      // unchanged (`isRecurringFlow` is false), so their wire value is identical.
      eventId: isRecurringFlow(state) ? state.selectedOccurrenceId! : event.id,
      // #652 — when the guest has built a basket, the LINES are the request and
      // the server derives partySize from them. The scalar fields are still
      // sent for a single-select / non-tiered event, which is every event a
      // venue has not given tiers.
      partySize: state.partySize,
      tierId: state.selectedTierId || undefined,
      kidsCount: state.kidsCount || 0,
      ...(state.ticketLines.length > 0 ? { ticketLines: state.ticketLines } : {}),
      // #816 — a STRING, never an amount. Omitted entirely when the field is
      // empty, so a full-price quote is byte-identical to the pre-#816 one.
      ...(couponCode ? { couponCode } : {}),
    });
    // r93 X2 — the basket was emptied while this was in flight: drop it.
    if (epoch !== ticketQuoteEpoch) return null;

    if (!res.ok) {
      // A 200-shaped coupon refusal (defensive — the contract says 400/409).
      // #816 FLAG-2 — only recurse when a code was actually sent. A refusal of a
      // CODE-LESS quote (a server bug, a mis-scoped error branch) would otherwise
      // re-quote forever: a request storm from the guest's browser.
      if ((res as { reason?: string }).reason === 'coupon_rejected' && couponCode) {
        return handleCouponRejection(
          state,
          update,
          (res as { error?: string }).error || 'That promo code could not be applied.',
        );
      }
      update({
        ticketQuote: null,
        ticketAvailability: null,
        ticketQuoteError: ticketQuoteMessage(res.reason),
        couponApplying: false,
        autoDeal: null,
      });
      return null;
    }

    update({
      ticketQuote: {
        unitPriceCents: res.unitPriceCents ?? null,
        kidsUnitPriceCents: res.kidsUnitPriceCents ?? null,
        kidsCount: res.kidsCount ?? 0,
        // #816 — the server's own money. The widget multiplies nothing. With no
        // coupon the server sends neither field and neither is written, so a
        // full-price quote object is byte-identical to the pre-#816 one.
        ...(typeof res.subtotalCents === 'number' ? { subtotalCents: res.subtotalCents } : {}),
        ...(typeof res.discountCents === 'number' ? { discountCents: res.discountCents } : {}),
        totalCents: res.totalCents,
        currency: res.currency,
        tierLabel: res.tierLabel ?? null,
        ...(res.lines ? { lines: res.lines } : {}),
      },
      ticketAvailability: {
        capacity: res.availability.capacity,
        coversSold: res.availability.coversSold,
        remaining: res.availability.remaining,
        soldOut: res.availability.soldOut,
      },
      ticketQuoteError: res.availability.soldOut ? ticketQuoteMessage('sold_out') : null,
      // #816 — `coupon` is present ONLY when the server accepted a code. A
      // missing `ticketsRemaining` becomes null = UNKNOWN, never "unlimited".
      // #954 — an AUTO-applied deal also arrives as `coupon`, but it is NOT a
      // typed code: it goes to `autoDeal` (code-free, never re-sent as
      // `couponCode`), and the promo field stays empty and usable.
      couponApplied: res.coupon && res.autoApplied !== true
        ? {
            code: res.coupon.code,
            percentOff: res.coupon.percentOff,
            discountCents: res.coupon.discountCents,
            ticketsRemaining:
              typeof res.coupon.ticketsRemaining === 'number' ? res.coupon.ticketsRemaining : null,
            ...(typeof res.coupon.label === 'string' && res.coupon.label ? { label: res.coupon.label } : {}),
            ...(typeof res.coupon.kind === 'string' && res.coupon.kind ? { kind: res.coupon.kind } : {}),
          }
        : null,
      autoDeal: autoDealFromQuote(res),
      couponApplying: false,
      ...(opts.keepCouponError ? {} : { couponError: null }),
    });
    return { totalCents: res.totalCents, currency: res.currency };
  } catch (err) {
    if (epoch !== ticketQuoteEpoch) return null;
    // #816 — a coupon refusal (400 "never works" / 409 "the pool moved") is NOT
    // a quote failure. The widget renders the server's sentence and nothing
    // else: it does not branch on the status, and it never invents copy.
    const rejection = parseCouponRejection(err);
    // #816 FLAG-2 — `couponCode` guard: a refusal of a code-less quote falls
    // through to the ordinary quote-error path instead of recursing forever.
    if (rejection && couponCode) {
      return handleCouponRejection(state, update, rejection.message);
    }
    update({
      ticketQuote: null,
      ticketAvailability: null,
      ticketQuoteError: 'Unable to price tickets right now. Please try again.',
      couponApplying: false,
      autoDeal: null,
    });
    return null;
  }
}

/** The server's total from a successful quote — what the next checkout charges. */
interface QuotedTotal {
  totalCents: number;
  currency: string;
}

/**
 * #954 — read the event's AUTO deal off a successful quote. Copies the
 * server's words and numbers only; derives nothing.
 *  - `autoApplied: true` + `coupon`  → applied (label + discount, verbatim)
 *  - `deal`                          → a live deal that did not fit the basket
 *  - neither                         → no live deal (`null`; a typed code that
 *                                      replaced the deal also lands here)
 */
export function autoDealFromQuote(res: TicketQuoteResponse): BookingState['autoDeal'] {
  if (res.autoApplied === true && res.coupon) {
    return {
      applied: true,
      label: (typeof res.coupon.label === 'string' && res.coupon.label.trim()) || 'Discount',
      discountCents: typeof res.coupon.discountCents === 'number' ? res.coupon.discountCents : 0,
      reason: null,
      message: null,
    };
  }
  const deal = res.deal;
  if (deal && typeof deal === 'object' && typeof deal.message === 'string' && deal.message.trim()) {
    return {
      applied: false,
      label: typeof deal.label === 'string' ? deal.label : '',
      discountCents: 0,
      reason: typeof deal.reason === 'string' ? deal.reason : null,
      message: deal.message.trim(),
    };
  }
  return null;
}

/**
 * Show the refusal, then leave a full-price, purchasable order behind.
 * Resolves to the re-quoted total (null when the re-quote itself failed).
 */
async function handleCouponRejection(
  state: BookingState,
  update: StateUpdater,
  message: string,
): Promise<QuotedTotal | null> {
  update({ couponApplied: null, couponError: message, couponApplying: false });
  return performTicketQuote(state, update, '', { keepCouponError: true });
}

/**
 * #953 G3 re-review — the banner for a code dropped AT CHECKOUT. The guest is
 * on the contact step, where no total is drawn, and the next Continue charges
 * the full re-quoted price; so the banner names the code, carries the server's
 * reason verbatim, and states the new total. When the re-quote failed there is
 * no server figure to state, so none is invented.
 */
function couponDroppedMessage(code: string, reason: string, quoted: QuotedTotal | null): string {
  const trimmed = reason.trim();
  const sentence = /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  const lead = `Code ${code} couldn't be applied: ${sentence}`;
  return quoted
    ? `${lead} Your total is now ${formatMoney(quoted.totalCents, quoted.currency)}.`
    : `${lead} Your order is now at full price — please check the total before paying.`;
}

/**
 * Hold a seat for a ticketed event and set up payment. Mirrors the deposit flow
 * shape — the returned `clientSecret` / `authorizationUrl` drive the EXISTING
 * payment step. A 409 means the event sold out between quote and checkout.
 */
export async function submitTicketCheckout(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  const event = selectedEvent(state);
  if (!event?.ticketing) return;

  const idempotencyKey = state.idempotencyKey
    || (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  if (!state.idempotencyKey) update({ idempotencyKey });

  update({ loading: true, error: null });
  try {
    const res = await apiClient.checkoutTicket({
      locationId: state.locationId,
      // #130 — recurring series checks out against the opaque occurrence id; a
      // one-off sends `event.id` (byte-identical, `isRecurringFlow` is false).
      eventId: isRecurringFlow(state) ? state.selectedOccurrenceId! : event.id,
      tierId: state.selectedTierId || undefined,
      partySize: state.partySize,
      kidsCount: state.kidsCount || undefined,
      // #652 — the basket the guest actually built. Sent on checkout as well as
      // quote so the two cannot disagree: the server prices the LINES, and a
      // stale scalar partySize riding alongside them is ignored rather than
      // silently charged.
      ...(state.ticketLines.length > 0 ? { ticketLines: state.ticketLines } : {}),
      // #816 — ONLY a code the server already accepted at quote travels here.
      // A refused/unapplied code would 400 the checkout and cost the guest a
      // booking they were told they could still make at full price.
      ...(state.couponApplied ? { couponCode: state.couponApplied.code } : {}),
      // #954 — "the guest was SHOWN the auto deal". Only with no typed code (a
      // typed code replaces the deal), and only when the quote applied it. A
      // tripwire for the server's 409 AUTO_DEAL_CHANGED; never a discount input.
      ...(!state.couponApplied && state.autoDeal?.applied ? { autoApplied: true as const } : {}),
      contact: {
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        phone: state.phone,
        notes: state.notes || undefined,
      },
      idempotencyKey,
      consent: state.consentGiven
        ? { accepted: true, policyVersion: PRIVACY_POLICY_VERSION, acceptedAt: new Date().toISOString() }
        : undefined,
    });

    // Map the returned payment handle onto the existing payment step.
    //
    // NOTE (#480, D6): the payment step now renders by HANDLE SHAPE, so this
    // 'ghl' normalisation no longer selects the renderer — `ghlPaymentUrl`
    // (the deprecated alias for `authorizationUrl`) is what opens the hosted
    // page. The provider label is left as-is here because the ticket flow's
    // own contract is pinned by ticket-actions.test.ts; migrating ticketing
    // onto `authorizationUrl` is a separate, deliberate change.
    // G3 payments HIGH-3 — a replay that hands back NO payment handle must not
    // advance to the payment step. It is not the dropped-network corner it
    // looks like: Back from payment returns to `contact`, and Continue
    // re-submits on the SAME sticky key, so two ordinary clicks land here. The
    // payment step reads "no client secret" as "render card fields", so the
    // guest types their card into a form that can never succeed and the only
    // escape is a reload.
    //
    // Burning the key the way a dead deposit attempt does makes the next
    // submit a clean booking with the contact details kept. The durable fix is
    // server-side re-derivation of the handle from the stored deposit (the
    // deposit rail already does this); until then, do not strand the guest.
    // #816 — a 100%-comped order. The server settled it itself (`paid: true`,
    // `status: 'confirmed'`, `depositStatus: 'waived'`) and deliberately sent NO
    // payment handle, so there is no gateway step to wait for. Checked BEFORE
    // the handle-less-replay guard below: both look like "no clientSecret", but
    // this one is a completed purchase, not a dead end.
    // Deliberately NOT keyed on `status: 'confirmed'` alone: a handle-less
    // idempotent replay can also describe a confirmed reservation, and that one
    // is the #652 HIGH-3 dead end, not a completed purchase. `paid` /
    // `depositStatus: 'waived'` are the settled-order signals the contract
    // guarantees, and only they may skip the gateway.
    const comped =
      !res.clientSecret &&
      !res.authorizationUrl &&
      (res.paid === true || res.depositStatus === 'waived');
    const ticketDiscountLine = discountLineFromCheckout(res, state);
    if (comped) {
      update({
        loading: false,
        currentStep: 'confirm',
        ticketDiscountLine,
        ticketReservationId: res.reservationId,
        ticketDate: res.date,
        appointmentId: res.reservationId,
        confirmationNumber: res.confirmationNumber,
        paymentAmount: res.amountCents ?? 0,
        paymentCurrency: res.currency,
        paymentStatus: 'succeeded',
        paymentClientSecret: null,
        ghlPaymentUrl: null,
        depositConfig: null,
        depositType: null,
        error: null,
      });
      return;
    }

    if (res.idempotentReplay && !res.clientSecret && !res.authorizationUrl) {
      update({
        loading: false,
        error: 'That booking attempt could not be resumed. Please try again — your details are kept.',
        ...deadAttemptReset(),
      });
      return;
    }

    const provider: BookingState['paymentProvider'] = res.clientSecret
      ? 'stripe'
      : res.authorizationUrl
        ? 'ghl'
        : ((res.provider as BookingState['paymentProvider']) || state.paymentProvider);

    update({
      loading: false,
      currentStep: 'payment',
      ticketDiscountLine,
      ticketReservationId: res.reservationId,
      ticketDate: res.date,
      appointmentId: res.reservationId,
      confirmationNumber: res.confirmationNumber,
      paymentProvider: provider,
      paymentAmount: res.amountCents,
      paymentCurrency: res.currency,
      paymentClientSecret: res.clientSecret || null,
      ghlPaymentUrl: res.authorizationUrl || null,
      // The ticket purchase prices itself — clear any deposit framing so the
      // payment step shows the cover, not a "deposit" label.
      depositConfig: null,
      depositType: null,
    });
  } catch (err: any) {
    // #953 G3 — a coupon refused AT CHECKOUT (the pool moved between quote and
    // checkout: 409 COUPON_EXHAUSTED / _ALREADY_USED; or a 400 `coupon_rejected`
    // incl. COUPON_BELOW_MINIMUM). Checked FIRST: the 409s must not fall into
    // the sold-out branch below, and the code must not stay applied, or every
    // retry re-sends it and is refused again. Same handling as the quote path —
    // the server's sentence, the code dropped, a full-price re-quote — plus the
    // banner, because the guest is on the contact step where the coupon block
    // is not drawn. The key is KEPT: the server released its claim before
    // answering, so the next submit claims it afresh.
    // #954 (ruling Q4) — the auto deal the guest was SHOWN is no longer what
    // the server would give (exhausted, expired, deactivated, or the basket no
    // longer qualifies). The server refused BEFORE any claim, intent or hold.
    // Never a silent different charge: re-quote, and tell the guest the new
    // total on the step they are on (the contact step draws no price). The
    // next Continue then checks out against the fresh quote — and no longer
    // claims a deal that is gone. Checked before the generic 409 → "sold out".
    if (err instanceof WidgetApiError && err.code === 'AUTO_DEAL_CHANGED') {
      await handleAutoDealChanged(state, update, err);
      return;
    }

    const rejection = parseCouponRejection(err);
    if (rejection) {
      // #953 G3 re-review — a RETRYABLE answer (503 `coupon_unavailable`: the
      // server could not CHECK the code) is not a refusal of it. Dropping the
      // code would re-price the order at full price for a code that may be
      // perfectly good, so it stays applied and the guest sees the server's
      // try-again sentence; the next Continue re-sends it. Likewise only a code
      // the widget actually sent can be dropped and re-quoted (the #816 FLAG-2
      // guard: a code-less refusal must not storm /quote).
      if (rejection.retryable || !state.couponApplied) {
        update({ loading: false, error: rejection.message });
        return;
      }
      // A real refusal: drop the code, re-quote at full price, and SAY the new
      // price — the next Continue charges it. `loading` stays true until the
      // re-quote lands, so Continue cannot race ahead of the new total.
      const droppedCode = state.couponApplied.code;
      const quoted = await handleCouponRejection(state, update, rejection.message);
      update({ loading: false, error: couponDroppedMessage(droppedCode, rejection.message, quoted) });
      return;
    }

    // #953 G3 — `checkout_unresolved` (503): the server could not tell whether
    // the booking row landed. Its sentence is the honest one, and the
    // idempotency key is deliberately NOT touched: a retry inside the server's
    // stale-claim window must REPLAY the possibly-landed booking, never mint a
    // second one (for a comp, a second confirmed one).
    if (err?.code === 'checkout_unresolved') {
      update({ loading: false, error: checkoutUnresolvedMessage(err) });
      return;
    }

    // G3 payments HIGH-2 — a 409 is no longer only "sold out". The ticket rail
    // now answers `replay_lookup_failed` (a transient DynamoDB blip) and
    // `replay_unresolved` (an attempt that cannot be resumed) with 409s, and
    // flattening both to "this event just sold out" tells a guest something
    // FALSE about an event that has seats — the very thing the server refuses
    // to do for a basket refusal. The code carries the distinction; read it.
    //
    // #953 G3 LOW — `replay_unresolved` is a DEAD attempt: the same key gets the
    // same refusal for the server's whole stale-claim window, so the key is
    // discarded (as the deposit rail does, deposit-copy.ts) and the next
    // Continue starts a fresh booking with the details kept.
    // `replay_lookup_failed` is transient and keeps its key so a retry dedupes.
    //
    // A basket refusal is a 400 discriminated by `reason: 'invalid_basket'`
    // (event-tickets-api.ts) — NOT `code`, which the server never sends there.
    const code = err?.code;
    const invalidBasket = errorReason(err) === 'invalid_basket';
    update({
      loading: false,
      ...(code === 'replay_unresolved' ? deadAttemptReset() : {}),
      error:
        code === 'replay_lookup_failed'
          ? 'We could not confirm your booking just now. Please try again in a moment.'
          : code === 'replay_unresolved'
            ? 'That booking attempt could not be resumed. Please try again — your details are kept.'
            : invalidBasket
              ? 'That combination of tickets cannot be booked. Please adjust your selection.'
              : err?.status === 409
                ? 'Sorry — this event just sold out.'
                : 'Unable to reserve tickets. Please try again.',
    });
  }
}

/** The `reason` discriminator of a JSON error body. Never throws. */
function errorReason(err: unknown): string | undefined {
  if (!(err instanceof WidgetApiError)) return undefined;
  try {
    const parsed = JSON.parse(err.body) as { reason?: unknown };
    return typeof parsed?.reason === 'string' ? parsed.reason : undefined;
  } catch {
    return undefined;
  }
}

const AUTO_DEAL_CHANGED_FALLBACK =
  'The offer on your tickets has changed. Please check your total and try again.';

/** The server's own sentence from an error body (length-capped), or the fallback. */
function serverSentence(err: WidgetApiError, fallback: string): string {
  try {
    const parsed = JSON.parse(err.body) as { error?: unknown };
    const msg = typeof parsed?.error === 'string' ? parsed.error.trim() : '';
    if (msg && msg.length <= 300) return msg;
  } catch {
    // non-JSON body — fall back
  }
  return fallback;
}

/** Money for a guest sentence: the SERVER's cents, formatted, never computed. */
function formatTicketMoney(cents: number, currency: string): string {
  const major = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
    }).format(major);
  } catch {
    return `${(currency || 'USD').toUpperCase()} ${major.toFixed(2)}`;
  }
}

/**
 * #954 — 409 AUTO_DEAL_CHANGED at checkout: drop the stale deal, re-quote with
 * NO code (exactly what the guest asked for — the deal was code-free), and
 * show the server's sentence plus the fresh server total. The idempotency key
 * is kept: the server released it before answering.
 *
 * #954 widget-G3 N2 — `loading` stays ON until the re-quote returns (mirrors
 * the #953 coupon-refusal path), so Continue cannot resubmit before the guest
 * has been told the new total. a11y L1 — the banner is set ONCE, after the
 * re-quote (the sentence alone if the re-quote failed): the role=alert banner
 * would otherwise be announced twice.
 */
async function handleAutoDealChanged(
  state: BookingState,
  update: StateUpdater,
  err: WidgetApiError,
): Promise<void> {
  const sentence = serverSentence(err, AUTO_DEAL_CHANGED_FALLBACK);
  // The stale deal goes now — nothing may keep claiming it, even if the
  // re-quote below fails.
  update({ autoDeal: null });
  // keepCouponError: a refused code still in the field keeps its sentence.
  const quoted = await performTicketQuote(state, update, '', { keepCouponError: true });
  update({
    loading: false,
    error: quoted && quoted.totalCents > 0
      ? `${sentence} Your total is now ${formatTicketMoney(quoted.totalCents, quoted.currency)}.`
      : sentence,
  });
}

/**
 * #954 — the confirmation's discount line, from the server's CHECKOUT echo
 * (the amount actually charged against), never from the quote's arithmetic.
 * An auto deal reads by its label ("Buy 1 Get 1 Free"); a typed code reads as
 * "Promo code CODE" — the deal's own code is never shown to the guest.
 */
function discountLineFromCheckout(
  res: TicketCheckoutResponse,
  state: BookingState,
): BookingState['ticketDiscountLine'] {
  if (!(typeof res.discountCents === 'number' && res.discountCents > 0)) return null;
  if (res.autoApplied === true) {
    const label = state.autoDeal?.label?.trim();
    return { label: label || 'Discount', discountCents: res.discountCents };
  }
  const code = res.couponCode || state.couponApplied?.code;
  return { label: code ? `Promo code ${code}` : 'Discount', discountCents: res.discountCents };
}

/**
 * #953 G3 — the guest-facing sentence for a 503 `checkout_unresolved`. The
 * server's own words when it sent usable ones (length-capped, like
 * `guestMessageFromError`); otherwise the same instruction, never the generic
 * "try again" — an unqualified retry is exactly the advice that risks a
 * duplicate booking here.
 */
function checkoutUnresolvedMessage(err: unknown): string {
  const FALLBACK =
    'We could not confirm your booking just now. Please check your email before trying again.';
  if (!(err instanceof WidgetApiError)) return FALLBACK;
  try {
    const parsed = JSON.parse(err.body) as { error?: unknown };
    const msg = typeof parsed?.error === 'string' ? parsed.error.trim() : '';
    return msg.length > 0 && msg.length <= 200 ? msg : FALLBACK;
  } catch {
    return FALLBACK;
  }
}

/**
 * Verify a ticketed purchase completed payment, then land on confirmation.
 * Called after the payment step reports success (Stripe), or while polling a
 * redirect-gateway payment. Returns true once the charge is confirmed.
 *
 * In `quiet` mode (used for redirect polling) a not-yet-paid result is NOT
 * surfaced as an error — verify is expected to report `success: false` on every
 * poll until the gateway confirms, so it only errors on the explicit, non-quiet
 * post-Stripe call.
 */
export async function verifyTicketPurchase(
  state: BookingState,
  update: StateUpdater,
  opts: { quiet?: boolean } = {},
): Promise<boolean> {
  if (!state.ticketReservationId || !state.ticketDate) {
    // Nothing to verify — show what we already have.
    update({ currentStep: 'confirm' });
    return true;
  }

  if (!opts.quiet) update({ loading: true, error: null });
  try {
    const res = await apiClient.verifyTicket({
      locationId: state.locationId,
      reservationId: state.ticketReservationId,
      date: state.ticketDate,
    });

    if (res.success) {
      update({
        loading: false,
        currentStep: 'confirm',
        confirmationNumber: res.confirmationNumber || state.confirmationNumber,
      });
      return true;
    }

    // The payment landed but the hold had already lapsed (server classified the
    // lost confirm as refund_due). Never say "try again" here — a second checkout
    // would take a second payment. This is shown even on quiet polls: it is a
    // terminal state, not a not-yet-paid tick.
    if (res.refundDue) {
      update({
        loading: false,
        paymentStatus: 'failed',
        error:
          'Your payment was received after the seat hold expired, so the booking could not be confirmed. ' +
          'Please do not pay again — the venue will refund you.',
      });
      return false;
    }

    if (!opts.quiet) {
      update({
        loading: false,
        paymentStatus: 'failed',
        error: 'Payment could not be confirmed. Please try again.',
      });
    }
    return false;
  } catch {
    if (!opts.quiet) {
      update({
        loading: false,
        paymentStatus: 'failed',
        error: 'Payment could not be confirmed. Please try again.',
      });
    }
    return false;
  }
}

/** Advance to the next step, triggering any necessary side effects */
export async function goToNextStep(
  state: BookingState,
  update: StateUpdater,
): Promise<void> {
  const next = nextStep(state);
  if (!next) return;

  // ── Ticketed-event flow ────────────────────────────────────────────────────
  // Diverges from a normal booking: checkout HOLDS the seat + returns a payment
  // handle (driving the existing payment step), and verify confirms the charge.
  if (isTicketedFlow(state)) {
    // Contact → Payment: hold the seat and obtain the payment handle.
    if (state.currentStep === 'contact') {
      await submitTicketCheckout(state, update);
      return;
    }
    // Payment → Confirm: verify the prepaid charge cleared.
    if (state.currentStep === 'payment') {
      await verifyTicketPurchase(state, update);
      return;
    }
    update({ currentStep: next });
    return;
  }

  // ── Leaving contact — the precedence rule (§5.2) ──────────────────────────
  // Order is the contract: the new deposit rail WINS over the legacy
  // requirePayment rail (§4.3 row e — a venue configured for both must not
  // charge twice; `/book`'s 402 is the server-side backstop). With
  // `depositCheckout` false — every tenant, until the flag is flipped — this
  // block is byte-identical to the previous code.
  if (state.currentStep === 'contact') {
    if (state.depositCheckout) {
      await submitDepositCheckout(state, update);
      return;
    }
    if (state.requirePayment) {
      if (state.paymentProvider === 'ghl') {
        await requestGhlPaymentOrder(state, update);
      } else {
        // Stripe (or legacy)
        await requestPaymentIntent(state, update);
      }
      return;
    }
    // Contact → Confirm (no payment): submit the booking directly
    await submitBooking(state, update);
    return;
  }

  // Payment → Confirm
  if (state.currentStep === 'payment') {
    // A deposit checkout ALREADY created the reservation (status 'pending').
    // Submitting again would book a second table — verify + confirm instead.
    if (state.depositReservationId) {
      await verifyDepositPayment(state, update);
      return;
    }
    await submitBooking(state, update);
    return;
  }

  // #947 — Service → Contact for a fixed-date NON-ticketed event: the guest has
  // nothing to choose in Date & Time, so the booking takes the event's own date
  // + start, written in the shape the datetime step would write. No round-trip
  // here — `/book`'s server-side slot pre-check (pacing, online seats, blocks)
  // is the gate, exactly as for a slot picked off the grid.
  if (state.currentStep === 'service' && next === 'contact') {
    const fixed = fixedEventSlot(state);
    if (fixed) {
      update({ ...fixed, currentStep: next });
      return;
    }
  }

  update({ currentStep: next });

  // #947 — Service → Date & Time for a one-off event with a date but no start:
  // `_onEventSelect` preset `selectedDate` to the event's date, so load that
  // date's times now — the step opens ON the event date, not empty on today.
  if (state.currentStep === 'service' && next === 'datetime') {
    const ev = selectedEvent(state);
    if (ev && !ev.recurring && state.selectedDate && state.selectedDate === ev.date) {
      await fetchAvailability({ ...state, currentStep: next }, update);
    }
  }
}
